import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Mic, Square, RotateCcw } from "lucide-react";

const fmtTime = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

/**
 * Records a voice note from the device mic (MediaRecorder). On stop it exposes the clip for playback,
 * and the user can delete + re-record before it's used. Emits the recorded audio as a File via
 * onChange(file | null) so the parent's existing presigned-upload flow works unchanged.
 *
 * Props: onChange(File|null), disabled.
 */
function VoiceRecorder({ onChange, disabled = false }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [elapsed, setElapsed] = useState(0);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const stopStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  useEffect(() => {
    // Cleanup mic + object URL on unmount.
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      stopStream();
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      toast.error("Voice recording isn't supported in this browser.");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recorder.onstop = () => {
        const type = recorder.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        const ext = type.includes("ogg") ? "ogg" : type.includes("mp4") ? "mp4" : "webm";
        const file = new File([blob], `voice-note-${Date.now()}.${ext}`, { type });
        onChange?.(file);
        stopStream();
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((e) => e + 1), 1000);
    } catch (err) {
      toast.error("Could not access the microphone. Please allow mic permission and try again.");
    }
  };

  const stop = () => {
    try {
      recorderRef.current?.stop();
    } catch {
      /* no-op */
    }
    if (timerRef.current) clearInterval(timerRef.current);
    setRecording(false);
  };

  const remove = () => {
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    setAudioUrl(null);
    setElapsed(0);
    onChange?.(null);
  };

  // Recorded clip → playback + re-record/delete.
  if (audioUrl) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-gray-200 bg-gray-50 p-2">
        <audio src={audioUrl} controls className="h-9 flex-1" />
        <button
          type="button"
          disabled={disabled}
          onClick={remove}
          className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1.5 text-xs font-medium text-gray-700 hover:bg-white disabled:opacity-50"
          title="Delete and record again"
        >
          <RotateCcw size={14} /> Re-record
        </button>
      </div>
    );
  }

  // Recording in progress → timer + stop.
  if (recording) {
    return (
      <div className="flex items-center gap-3 rounded-md border border-rose-200 bg-rose-50 p-2">
        <span className="inline-flex items-center gap-2 text-sm font-medium text-rose-700">
          <span className="h-2.5 w-2.5 animate-pulse rounded-full bg-rose-500" />
          Recording… {fmtTime(elapsed)}
        </span>
        <button
          type="button"
          onClick={stop}
          className="ml-auto inline-flex items-center gap-1 rounded-md bg-rose-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-rose-700"
        >
          <Square size={13} /> Stop
        </button>
      </div>
    );
  }

  // Idle → record.
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={start}
      className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:border-flamingo-400 hover:bg-flamingo-50 disabled:opacity-50"
    >
      <Mic size={16} /> Record voice note
    </button>
  );
}

export default VoiceRecorder;
